import { HeroWaves } from "@/components/hero-waves";
import { JobCard } from "@/components/jobs/job-card";
import { Band, Caption, Display, Eyebrow, GRID_PAPER, Rail } from "@/components/landing/primitives";
import { useAuthModal } from "@/context/auth-modal";
import { getUser } from "@/functions/get-user";
import { authClient } from "@/lib/auth-client";
import { client } from "@/utils/orpc";
import { CREDIT_PLANS } from "@super-resume-new/auth/lib/plans";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from "@super-resume-new/ui/components/accordion";
import { Button } from "@super-resume-new/ui/components/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  Blocks,
  Briefcase,
  Check,
  CheckCircle2,
  Code2,
  Copy,
  Cpu,
  Database,
  FileCheck2,
  FileText,
  HelpCircle,
  Layers,
  LineChart,
  ListTodo,
  Lock,
  Quote,
  ScanLine,
  ShieldCheck,
  Sparkles,
  Terminal,
  UserCheck,
  Wand2,
  XCircle,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { jsonLdScript, seo } from "@/utils/seo";
import { softwareApplicationSchema } from "@/utils/schema";

const HOME_TITLE = "JobCues: Curated Jobs, Free ATS Match & AI Resume Tailor";
const HOME_DESCRIPTION =
  "Find verified tech jobs with decoded briefs, run free ATS match checks, tailor high-impact resumes using your Master Career Profile, and automatically track applications to offer.";

export const Route = createFileRoute("/")({
  beforeLoad: async () => {
    const session = await getUser();
    return { session };
  },
  head: () => {
    const head = seo({
      title: HOME_TITLE,
      description: HOME_DESCRIPTION,
      path: "/",
    });
    return {
      ...head,
      scripts: [
        jsonLdScript(
          softwareApplicationSchema({
            name: "JobCues",
            description: HOME_DESCRIPTION,
            path: "/",
            featureList: [
              "Evidence-based AI resume rewriting against a specific job description",
              "ATS match scoring with matched and missing keywords",
              "12 ATS-safe resume templates",
              "Verified tech job directory with per-job match checks",
              "Job application tracker",
              "REST API and Model Context Protocol server",
            ],
          }),
        ),
      ],
    };
  },
  component: LandingPageComponent,
});

const TEMPLATES = [
  { id: "ats", name: "ATS Clean", desc: "Minimalist, zero-clutter parsing favorite" },
  { id: "modern", name: "Modern Tech", desc: "Sleek typography with structured sections" },
  {
    id: "classic",
    name: "Classic Corporate",
    desc: "Timeless layout preferred by traditional firms",
  },
  { id: "executive", name: "Executive", desc: "High-density hierarchy for senior & staff roles" },
  { id: "finely", name: "Finely Styled", desc: "Refined aesthetic with crisp visual balance" },
  { id: "acorn", name: "Acorn", desc: "Contemporary compact design with high scan speed" },
  { id: "lavandula", name: "Lavandula", desc: "Subtle elegance with modern header accents" },
  { id: "soft-hard", name: "Soft & Hard", desc: "Distinct skill categorization and tech stacks" },
  { id: "timeline", name: "Timeline", desc: "Chronological trajectory for rapid visual scanning" },
  { id: "ttq", name: "TTQ Compact", desc: "Maximum content density in a tight, compact layout" },
  { id: "imprecv", name: "ImpreCV", desc: "Bold headlines with high-contrast deliverables" },
  { id: "nabcv", name: "NabCV", desc: "Balanced dual-column format for experienced pros" },
];

interface EvidenceExample {
  role: string;
  label: string;
  icon: typeof Code2;
  before: {
    text: string;
    atsScore: number;
    issues: string[];
  };
  after: {
    text: string;
    atsScore: number;
    keywords: string[];
    metrics: string[];
    defendCard: {
      topic: string;
      advice: string;
    };
  };
}

const EVIDENCE_EXAMPLES: Record<string, EvidenceExample> = {
  frontend: {
    role: "Frontend Engineer",
    label: "Frontend / React",
    icon: Code2,
    before: {
      text: "Worked on the frontend of our web application using React and helped fix bugs and make the site faster for users.",
      atsScore: 34,
      issues: [
        "No quantifiable business impact or performance benchmark.",
        "Missing target keywords (TypeScript, Next.js, Core Web Vitals, SSR).",
        "Passive action verbs fail recruiter screening criteria.",
      ],
    },
    after: {
      text: "Architected customer checkout portal in React and TypeScript, optimizing Core Web Vitals to reduce Largest Contentful Paint (LCP) by 42% and increase transaction conversion for 45k daily users.",
      atsScore: 96,
      keywords: ["React", "TypeScript", "Core Web Vitals", "LCP", "Architecture"],
      metrics: ["42% reduction", "45k daily users"],
      defendCard: {
        topic: "Core Web Vitals & React Optimization",
        advice:
          "Highlight dynamic import splitting, memoization of re-renders, and server-side image compression that drove the 42% LCP reduction during technical interviews.",
      },
    },
  },
  backend: {
    role: "Distributed Backend Engineer",
    label: "Backend / Distributed Systems",
    icon: Database,
    before: {
      text: "Built backend API endpoints with Go and PostgreSQL and maintained microservices to keep the system running reliably.",
      atsScore: 38,
      issues: [
        "Zero mention of throughput, latency SLA, or concurrency scale.",
        "Omits infrastructure keywords (Kafka, Redis, Kubernetes, gRPC).",
        "Describes maintenance tasks rather than engineered solutions.",
      ],
    },
    after: {
      text: "Engineered distributed streaming microservices in Go and gRPC with Apache Kafka and PostgreSQL, scaling throughput to 18,000 req/sec while maintaining 99.99% SLA and p99 latency <18ms.",
      atsScore: 95,
      keywords: ["Go", "gRPC", "Apache Kafka", "PostgreSQL", "Microservices"],
      metrics: ["18,000 req/sec", "99.99% SLA", "p99 <18ms"],
      defendCard: {
        topic: "Kafka Partitioning & High Concurrency",
        advice:
          "Explain your consumer group partitioning strategy and database connection pooling that kept p99 latency sub-18ms under peak load.",
      },
    },
  },
  ai: {
    role: "AI / Machine Learning Engineer",
    label: "AI / ML Pipelines",
    icon: Cpu,
    before: {
      text: "Created AI models with Python and PyTorch for our document processing product and tested prompt engineering.",
      atsScore: 31,
      issues: [
        "No mention of inference latency, batching, GPU optimization, or RAG.",
        "Missing foundational pipeline tech (vLLM, LangChain, Vector DB).",
        "Vague 'prompt engineering' label lacks rigor.",
      ],
    },
    after: {
      text: "Deployed low-latency RAG inference pipeline using Python, PyTorch, and vLLM with pgvector embeddings, accelerating document retrieval throughput 4.5x and reducing token compute cost by 38%.",
      atsScore: 97,
      keywords: ["Python", "PyTorch", "vLLM", "RAG", "pgvector", "Embeddings"],
      metrics: ["4.5x throughput", "38% cost reduction"],
      defendCard: {
        topic: "vLLM PagedAttention & RAG Architecture",
        advice:
          "Discuss continuous batching with vLLM PagedAttention and vector indexing trade-offs between HNSW and IVFFlat.",
      },
    },
  },
};

/** 4 core pillars showcased in the hero section. */
const HERO_PILLARS = [
  {
    id: "jobs" as const,
    step: "01",
    name: "Curated Jobs",
    tagline: "Hourly verified roles with decoded briefs",
    icon: Briefcase,
  },
  {
    id: "match" as const,
    step: "02",
    name: "Free ATS Match Check",
    tagline: "Instant ATS score & keyword gap scan",
    icon: ScanLine,
  },
  {
    id: "tailor" as const,
    step: "03",
    name: "Resume Tailor & ATS Booster",
    tagline: "Master Profile evidence + Defend Cards",
    icon: Wand2,
  },
  {
    id: "tracker" as const,
    step: "04",
    name: "Auto Tracker",
    tagline: "Pipeline stages with resume version memory",
    icon: ListTodo,
  },
];

/** Applicant tracking systems the verified feed is sourced from. */
const SOURCES = ["Ashby", "Greenhouse", "Lever", "LinkedIn"];

/** What the engine pulls out of every posting it reads. */
const BRIEF_PARTS = [
  {
    icon: Quote,
    label: "The one-liner",
    body: "Who is hiring, for what, and the single detail that makes the role distinctive.",
  },
  {
    icon: ScanLine,
    label: "A straight fit read",
    body: "What level this really is versus what it is titled, who it suits, and who should not bother.",
  },
  {
    icon: ListTodo,
    label: "The actual day-to-day",
    body: "What you would build or run, who you report to, team size, on-call and travel.",
  },
  {
    icon: Layers,
    label: "Must-have vs nice-to-have",
    body: "Requirements split from the wish list, so one missing bonus skill never stops you applying.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Enter everything",
    body: "Upload your resume to fill the profile, then add every other project, skill, and role. It is an archive, so it holds far more than a resume does.",
  },
  {
    n: "02",
    title: "Let the tailor select",
    body: "For each job, the AI picks only the most relevant items from your archive and rewrites them around that description.",
  },
  {
    n: "03",
    title: "Send and track",
    body: "Export a focused, professional PDF, then follow it from Bookmarked through Offered with the exact variant you sent.",
  },
];

const SUITE = [
  {
    icon: ScanLine,
    title: "Match report",
    body: "Scores your resume against a job description and lists the exact keywords and evidence it is missing.",
    to: "/resume-tailor" as const,
    mock: (
      <div className="w-full max-w-56 space-y-2.5">
        <div className="flex items-baseline justify-between">
          <span className="font-mono text-2xl font-semibold text-foreground">96%</span>
          <span className="font-mono text-[10px] text-muted-foreground">match</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
          <div className="h-full w-[96%] rounded-full bg-violet-500" />
        </div>
        <div className="flex flex-wrap gap-1">
          {["React", "gRPC", "p99"].map((k) => (
            <span
              key={k}
              className="rounded border border-border bg-card px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"
            >
              {k}
            </span>
          ))}
          <span className="rounded border border-destructive/30 bg-destructive/10 px-1.5 py-0.5 font-mono text-[10px] text-destructive">
            missing: Kafka
          </span>
        </div>
      </div>
    ),
  },
  {
    icon: Zap,
    title: "ATS PDF Booster",
    body: "Keeps your existing PDF design pixel-identical while writing ATS keywords into the machine-readable layer.",
    to: "/ats-booster" as const,
    mock: (
      <div className="relative w-32 rounded-md border border-border bg-card p-3 shadow-sm">
        <div className="space-y-1.5">
          <div className="h-1.5 w-2/3 rounded-full bg-border" />
          <div className="h-1 w-full rounded-full bg-border/70" />
          <div className="h-1 w-5/6 rounded-full bg-border/70" />
          <div className="h-1 w-full rounded-full bg-border/70" />
          <div className="h-1 w-3/4 rounded-full bg-border/70" />
        </div>
        <span className="absolute -right-6 bottom-3 rounded border border-violet-500/30 bg-violet-500/10 px-1.5 py-0.5 font-mono text-[10px] text-violet-600 dark:text-violet-400">
          +keywords
        </span>
      </div>
    ),
  },
  {
    icon: ShieldCheck,
    title: "Defend Cards",
    body: "Each promoted bullet ships with the talking points you need to explain it in a screening call.",
    to: "/resume-tailor" as const,
    mock: (
      <div className="w-full max-w-56 rounded-md border border-border bg-card p-3 shadow-sm">
        <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold text-foreground">
          <ShieldCheck className="size-3 text-violet-500" aria-hidden="true" />
          Kafka partitioning
        </div>
        <div className="space-y-1.5">
          <div className="h-1 w-full rounded-full bg-border" />
          <div className="h-1 w-11/12 rounded-full bg-border" />
          <div className="h-1 w-2/3 rounded-full bg-border" />
        </div>
      </div>
    ),
  },
  {
    icon: UserCheck,
    title: "Master Career Profile",
    body: "Upload your resume once, then keep adding projects, roles, and metrics. Tailor picks what each job needs.",
    to: "/account" as const,
    mock: (
      <div className="w-full max-w-56 space-y-1.5">
        {["linkedin.com/in/you", "github.com/you", "notes.md"].map((src) => (
          <div
            key={src}
            className="flex items-center justify-between rounded-md border border-border bg-card px-2.5 py-1.5 font-mono text-[10px] text-muted-foreground"
          >
            <span className="truncate">{src}</span>
            <Check className="size-3 shrink-0 text-emerald-500" aria-hidden="true" />
          </div>
        ))}
      </div>
    ),
  },
];

const FAQS = [
  {
    value: "how-it-works",
    q: "How does the resume tailoring actually work?",
    a: "It is a redistribution engine, not a text polisher. It reads the literal requirements out of the job description, then rebuilds your bullets under a fixed budget so the strongest evidence wins each line. Every bullet lands in the same shape: verb, a deliverable a non-technical reader can picture, the keywords, and a plain reason it mattered. Your facts survive; your sentences do not.",
  },
  {
    value: "honesty",
    q: "Will the AI invent credentials on my resume?",
    a: "No. It never names a vendor, product, certification, employer, or degree you have had zero contact with. Those go to a gap questionnaire instead, which asks you the question only you can answer. Anything the rewrite does stretch ships with a Defend Card that gives you the fact underneath the claim and the answer to give when someone asks.",
  },
  {
    value: "modes",
    q: "What do the three rewrite Modes do?",
    a: "Mode sets how far the rewrite may travel from what your resume literally says. Light only relocates words you already wrote and lands around 50% keyword coverage. Balance is the default, adds terms that are true by construction, and reaches 70–80%. Aggressive goes past 90% by promoting listed skills into real bullets, and every stretched claim there comes with a Defend Card.",
  },
  {
    value: "tools",
    q: "Should I use the Resume Tailor or the ATS Booster?",
    a: "The Tailor rewrites the content of your resume to fit the job. The Booster leaves your document looking exactly as it is and embeds the job's keywords into the text an ATS parser extracts. Use the Booster when your resume is already well-written and only needs to clear the filter. Use the Tailor when the content itself needs to align with the description.",
  },
  {
    value: "jobs",
    q: "Where do the jobs come from, and what do you change about them?",
    a: "They are pulled from company boards on Ashby, Greenhouse, Lever, and LinkedIn, and filtered to roles posted in the last 7 days. We then read each posting and republish it as a fixed brief: a one-line summary, a straight read on what level it really is and who it suits, the factual day-to-day, and required skills split from the nice-to-have list.",
  },
  {
    value: "master-profile",
    q: "What is the Master Career Profile?",
    a: "One archive holding everything you have done: every project, role, skill, and number. It is an archive rather than a resume, so it holds far more than one page. Upload your existing resume and it is parsed straight in, then you keep adding. When you tailor for a job, the AI reads the whole archive and pulls only the handful of items that fit, so you never re-paste your history for each application.",
  },
  {
    value: "credits",
    q: "How do the monthly plans and credits work?",
    a: "One credit covers one full rewrite or one boost. Starter is $5/mo for 100 credits, Pro is $10/mo for 250 credits plus MCP and REST API access, and Ultimate is $20/mo for 1000 credits with the same MCP and API access. Credits refresh every month, signup includes 3 free ones, and you can switch or cancel at any time.",
  },
];

function LandingPageComponent() {
  const { data: session } = authClient.useSession();
  const { openAuthModal } = useAuthModal();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedTemplate, setSelectedTemplate] = useState("ats");
  const [activeHeroPillar, setActiveHeroPillar] = useState<"jobs" | "match" | "tailor" | "tracker">("jobs");
  const [isHeroAutoPlaying, setIsHeroAutoPlaying] = useState(true);

  // Auto-advance hero carousel every 5s until user manually clicks
  useEffect(() => {
    if (!isHeroAutoPlaying) return;

    const timer = setInterval(() => {
      setActiveHeroPillar((prev) => {
        const currentIndex = HERO_PILLARS.findIndex((p) => p.id === prev);
        const nextIndex = (currentIndex + 1) % HERO_PILLARS.length;
        return HERO_PILLARS[nextIndex].id;
      });
    }, 5000);

    return () => clearInterval(timer);
  }, [isHeroAutoPlaying, activeHeroPillar]);

  const selectHeroPillar = (id: "jobs" | "match" | "tailor" | "tracker") => {
    setIsHeroAutoPlaying(false);
    setActiveHeroPillar(id);
  };

  const [activeMcpTab, setActiveMcpTab] = useState<"mcp" | "agent" | "curl">("mcp");
  const [activeRoleKey, setActiveRoleKey] = useState<"frontend" | "backend" | "ai">("frontend");
  const [copiedMcp, setCopiedMcp] = useState(false);

  // Query recent verified jobs for the live jobs showcase
  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ["landing", "recentJobs"],
    queryFn: () => client.jobs.list({ limit: 4, page: 1 }),
  });

  // Saved job IDs for bookmarking in minimal job cards
  const { data: savedIds = [] } = useQuery({
    queryKey: ["jobs", "savedIds"],
    queryFn: async () => client.jobs.getSavedIds(),
  });
  const savedSet = new Set(savedIds);

  const toggleSaveMutation = useMutation({
    mutationFn: async (jobId: string) => client.jobs.toggleSave({ jobId }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["jobs", "savedIds"] });
      toast.success(
        res.saved ? "Job saved to your bookmarks!" : "Job removed from saved bookmarks.",
      );
    },
    onError: () => {
      toast.error("Failed to update saved job. Please try again.");
    },
  });

  const handleSignupCta = () => {
    if (session?.user) {
      navigate({ to: "/jobs" });
    } else {
      openAuthModal({
        defaultTab: "signup",
        title: "Get Started with JobCues",
        description:
          "Sign up today to explore verified tech jobs and receive 3 free AI tailoring credits.",
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedMcp(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedMcp(false), 2000);
  };

  const activeEvidence = EVIDENCE_EXAMPLES[activeRoleKey] || EVIDENCE_EXAMPLES.frontend;

  const mcpConfigCode = `{
  "mcpServers": {
    "jobcues": {
      "command": "npx",
      "args": ["-y", "@jobcues/mcp-server"],
      "env": {
        "JOBCUES_API_KEY": "jc_live_your_api_key_here"
      }
    }
  }
}`;

  const agentPromptCode = `// In Cursor Composer or Claude Desktop:
"Check the latest verified Frontend roles in JobCues matching Next.js,
tailor my resume against Job #8921 using the 'ats' template,
and export the PDF."

// AI Agent response via MCP:
> Executing tool: jobcues_tailor_resume(jobId: "8921", template: "ats")
> Rendering ATS layout... Done (3.8s)
> Match Score: 96% | PDF saved to ./build/resume_stripe_frontend.pdf`;

  const curlApiCode = `curl -X POST https://jobcues.com/api/rpc/resume.tailor \\
  -H "Authorization: Bearer jc_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "jobDescription": "Senior Frontend Engineer with React…",
    "template": "ats",
    "mode": "balanced"
  }'`;

  const roleKeys = Object.keys(EVIDENCE_EXAMPLES) as Array<"frontend" | "backend" | "ai">;

  return (
    <div className="relative">
      {/* 1. HERO */}
      <Band className="relative w-full overflow-hidden border-t-0">
        <HeroWaves />
        <Rail className="relative z-10">
          <div className="grid gap-10 px-6 py-16 sm:px-10 sm:py-20 lg:grid-cols-12 lg:items-end dark:bg-neutral-900/20">
            <div className="lg:col-span-7">
              <Eyebrow icon={Sparkles} className="mb-6">
                Curated Jobs · Free Match Checks · 1-Click Tailor & Tracker
              </Eyebrow>
              <Display as="h1" className="text-4xl sm:text-5xl lg:text-[3rem]">
                <strong>Find verified jobs. Check your ATS match.</strong>
                <br />
                Tailor resumes that <strong>win interviews.</strong>
              </Display>
            </div>

            <div className="lg:col-span-5">
              <p className="max-w-xl text-md leading-relaxed text-muted-foreground">
                Stop getting ghosted by automated screening filters. JobCues combines a <span className="font-medium text-blue-600 dark:text-blue-400 bg-blue-500/10 py-0.5 px-1 rounded">
                  <span className="inline-block mx-0.5 -mb-0.5"><Briefcase className="size-3.5" /></span> <span>curated feed</span></span> of verified
                tech roles with a free <span className="font-medium text-amber-600 dark:text-amber-400 bg-amber-500/5 py-0.5 px-1 rounded">
                  <span className="inline-block mx-0.5"><ScanLine className="size-3.5 -mb-0.5" /></span>ATS checker</span>, <span className="font-medium text-violet-600 dark:text-violet-400 bg-violet-500/5 py-0.5 px-1 rounded">
                    <span className="inline-block mx-0.5"><Wand2 className="size-3.5 -mb-0.5" /></span>1-click tailor</span> & <span className="font-medium text-green-600 dark:text-green-400 bg-green-500/5 py-0.5 px-1 rounded">
                      <span className="inline-block mx-0.5"><Zap className="size-3.5 -mb-0.25" /></span>ATS Booster</span> powered by your Master Career Profile
                and interview Defend Cards, with an auto <span className="font-medium text-pink-600 dark:text-pink-400 bg-pink-500/10 py-0.5 px-1 rounded">
                  <span className="inline-block mx-0.5"><ListTodo className="size-3.5 -mb-0.5" /></span>application tracker</span>.
              </p>

              <div className="w-full mt-6 flex flex-wrap items-center gap-2.5">
                <Link to="/jobs" className="w-full sm:w-auto">
                  <Button className="h-10 gap-2 font-semibold w-full sm:w-auto">
                    <Briefcase className="size-4" />
                    Explore verified jobs
                    <ArrowRight className="size-4" />
                  </Button>
                </Link>
                <Link to="/resume-tailor" className="w-full sm:w-auto">
                  <Button variant="outline" className="h-10 gap-2 font-semibold w-full sm:w-auto">
                    <Wand2 className="size-4 text-violet-500" />
                    Resume tailor
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Keyframe animation for carousel progress bar */}
          <style>{`
            @keyframes heroPillarProgress {
              from { width: 0%; }
              to { width: 100%; }
            }
          `}</style>

          {/* 4 Pillars Tab Rail */}
          <div className="grid grid-cols-2 border-t border-border sm:grid-cols-4">
            {HERO_PILLARS.map((pillar) => {
              const Icon = pillar.icon;
              const isActive = activeHeroPillar === pillar.id;
              return (
                <button
                  key={pillar.id}
                  type="button"
                  onClick={() => selectHeroPillar(pillar.id)}
                  aria-pressed={isActive}
                  className={`relative flex cursor-pointer flex-col gap-1 overflow-hidden px-4 py-3.5 text-left transition sm:px-3 sm:py-3 ${
                    isActive
                      ? "bg-muted/40 text-foreground shadow-xs"
                      : "text-muted-foreground hover:bg-muted/15 hover:text-foreground"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {/*<span className="font-mono text-[10px] font-semibold text-violet-500">
                      {pillar.step}
                    </span>*/}
                    <Icon className="size-3.5 shrink-0" aria-hidden="true" />
                    <span className="truncate text-sm font-semibold">{pillar.name}</span>
                  </div>
                  <span className="hidden text-xs text-muted-foreground sm:line-clamp-1">
                    {pillar.tagline}
                  </span>

                  {/* Bottom line: shows 5s progress bar when auto-playing, solid line when paused */}
                  <div className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-border/40">
                    {isActive && (
                      <div
                        key={`${pillar.id}-${isHeroAutoPlaying ? "auto" : "paused"}`}
                        className="h-full bg-violet-600 dark:bg-violet-400"
                        style={{
                          width: isHeroAutoPlaying ? undefined : "100%",
                          animation: isHeroAutoPlaying
                            ? "heroPillarProgress 5000ms linear forwards"
                            : "none",
                        }}
                      />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Interactive preview on graph paper */}
          <div className={`border-t border-border p-5 sm:p-10 ${GRID_PAPER}`}>
            {activeHeroPillar === "jobs" && (
              <div className="rounded-xl border border-border bg-card shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <span className="size-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
                    <span className="text-xs font-semibold text-foreground">Curated Job Brief</span>
                    <span className="text-[11px] text-muted-foreground">Ashby Verified · Posted 14h ago</span>
                  </div>
                  <span className="rounded-md border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 font-mono text-[11px] text-emerald-700 dark:text-emerald-400">
                    Direct Board Link · No Boilerplate
                  </span>
                </div>

                <div className="p-5 sm:p-6">
                  <div className="flex flex-col gap-2 pb-5 border-b border-border sm:flex-row sm:items-baseline sm:justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground">Stripe</span>
                        <span className="text-muted-foreground">·</span>
                        <h3 className="text-base font-semibold text-foreground sm:text-lg">
                          Staff Distributed Systems Engineer
                        </h3>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs font-mono">
                        <span className="rounded border border-border bg-muted px-2 py-0.5 text-foreground">
                          $195k – $245k base
                        </span>
                        <span className="rounded border border-border bg-muted px-2 py-0.5 text-foreground">
                          Remote (US / Canada)
                        </span>
                        <span className="rounded border border-border bg-muted px-2 py-0.5 text-foreground">
                          L6 Staff Scope
                        </span>
                      </div>
                    </div>
                    <Link to="/jobs" className="self-start sm:self-auto">
                      <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs font-semibold">
                        View in job feed
                        <ArrowRight className="size-3" />
                      </Button>
                    </Link>
                  </div>

                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    <div className="rounded-lg border border-border bg-muted/40 p-3.5">
                      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-foreground">
                        <Quote className="size-3.5 text-violet-500" />
                        The One-Liner
                      </div>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        Leading core payment ledger reliability handling 50,000+ financial transactions/sec with zero downtime tolerance.
                      </p>
                    </div>

                    <div className="rounded-lg border border-border bg-muted/40 p-3.5">
                      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-foreground">
                        <ScanLine className="size-3.5 text-violet-500" />
                        A Straight Fit Read
                      </div>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        True L6 Staff level. Built for engineers with deep distributed consensus, Kafka partitioning, and low-latency storage experience.
                      </p>
                    </div>

                    <div className="rounded-lg border border-border bg-muted/40 p-3.5">
                      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-foreground">
                        <ListTodo className="size-3.5 text-violet-500" />
                        The Actual Day-to-Day
                      </div>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        Core Go/gRPC services, multi-region database replication, architecture reviews, and p99 SLA guarantees (&lt;18ms). 15% on-call rotation.
                      </p>
                    </div>

                    <div className="rounded-lg border border-border bg-muted/40 p-3.5">
                      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-foreground">
                        <Layers className="size-3.5 text-violet-500" />
                        Requirements vs Wishlist
                      </div>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        <strong className="text-foreground">Must-have:</strong> Go, Apache Kafka, Distributed Systems, Concurrency.<br />
                        <strong className="text-foreground">Bonus:</strong> Kubernetes operators, Rust, eBPF telemetry.
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4 text-xs text-muted-foreground">
                    <span>We read every posting hourly so you can evaluate fit in 10 seconds instead of scrolling through boilerplate.</span>
                    <button
                      type="button"
                      onClick={() => selectHeroPillar("match")}
                      className="cursor-pointer font-semibold text-violet-600 hover:text-violet-500 dark:text-violet-400"
                    >
                      Check ATS match against this role →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeHeroPillar === "match" && (
              <div className="rounded-xl border border-border bg-card shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <span className="size-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
                    <span className="text-xs font-semibold text-foreground">Free ATS Match Checker</span>
                    <span className="text-[11px] text-muted-foreground">Resume vs Target Job Description</span>
                  </div>
                  <span className="font-mono text-[11px] text-emerald-600 dark:text-emerald-400">
                    Instant ATS Audit · 100% Free
                  </span>
                </div>

                <div className="grid divide-y divide-border md:grid-cols-12 md:divide-x md:divide-y-0">
                  {/* Left: Score Gauge */}
                  <div className="p-5 sm:p-6 md:col-span-5 flex flex-col justify-between">
                    <div>
                      <div className="flex items-baseline justify-between">
                        <span className="font-mono text-4xl font-bold tracking-tight text-foreground">
                          94%
                        </span>
                        <span className="rounded-md border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 font-mono text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                          Strong ATS Match
                        </span>
                      </div>
                      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-border">
                        <div className="h-full w-[94%] rounded-full bg-gradient-to-r from-violet-500 to-emerald-500" />
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        Top 5% candidate alignment. Resume clears automated ATS screening filters with high confidence.
                      </p>
                    </div>

                    <div className="mt-6 space-y-2 border-t border-border pt-4">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Keyword Coverage</span>
                        <span className="font-mono font-medium text-foreground">14 / 16 (88%)</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Seniority Alignment</span>
                        <span className="font-mono font-medium text-foreground">Staff / Principal (Match)</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">ATS Format Compatibility</span>
                        <span className="font-mono font-medium text-emerald-600 dark:text-emerald-400">100% ATS-Safe</span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Skills & Gap Analysis */}
                  <div className="p-5 sm:p-6 md:col-span-7 flex flex-col justify-between">
                    <div className="space-y-4">
                      <div>
                        <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          <CheckCircle2 className="size-3.5 text-emerald-500" aria-hidden="true" />
                          Matched Target Keywords (14 found in resume)
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {[
                            "Go",
                            "gRPC",
                            "Apache Kafka",
                            "PostgreSQL",
                            "Distributed Systems",
                            "Microservices",
                            "High Throughput",
                            "p99 Latency SLAs",
                          ].map((kw) => (
                            <span
                              key={kw}
                              className="inline-flex items-center gap-1 rounded-md border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 font-mono text-[11px] text-emerald-700 dark:text-emerald-400"
                            >
                              <Check className="size-3" />
                              {kw}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          <XCircle className="size-3.5 text-destructive" aria-hidden="true" />
                          Missing Critical Keywords (Gaps to address)
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {["Multi-region replication", "Continuous Batching"].map((kw) => (
                            <span
                              key={kw}
                              className="rounded-md border border-destructive/30 bg-destructive/10 px-2 py-0.5 font-mono text-[11px] text-destructive"
                            >
                              missing: {kw}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="rounded-lg border border-border bg-muted/60 p-3 text-xs text-muted-foreground">
                        <span className="font-semibold text-foreground">ATS Gap Advice: </span>
                        Recruiters screen for multi-region clustering. Use the 1-click tailor to pull relevant projects from your Master Profile and close these gaps before applying.
                      </div>
                    </div>

                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
                      <Link to="/jobs">
                        <Button size="sm" className="h-8 gap-1.5 text-xs font-semibold">
                          <ScanLine className="size-3.5" />
                          Check your resume free
                          <ArrowRight className="size-3" />
                        </Button>
                      </Link>
                      <button
                        type="button"
                        onClick={() => selectHeroPillar("tailor")}
                        className="cursor-pointer text-xs font-semibold text-violet-600 hover:text-violet-500 dark:text-violet-400"
                      >
                        Fix gaps in 1-Click →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeHeroPillar === "tailor" && (
              <div className="rounded-xl border border-border bg-card shadow-sm">
                {/* Header with Title & Quick Role Switcher */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <span className="size-2 rounded-full bg-emerald-500" aria-hidden="true" />
                    <span className="text-xs font-semibold text-foreground">1-Click Resume Tailor & ATS Booster</span>
                    <span className="text-[11px] text-muted-foreground">Master Profile → ATS-proof resume</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {roleKeys.map((key) => {
                      const item = EVIDENCE_EXAMPLES[key];
                      const isActive = activeRoleKey === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            setIsHeroAutoPlaying(false);
                            setActiveRoleKey(key);
                          }}
                          className={`cursor-pointer rounded-md px-2.5 py-1 text-[11px] font-medium transition ${
                            isActive
                              ? "bg-muted font-semibold text-foreground shadow-xs"
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {item.role.split(" ")[0]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 3-Step At-A-Glance Workflow Strip */}
                <div className="grid grid-cols-1 divide-y divide-border border-b border-border bg-muted/20 text-xs sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                  <div className="flex items-center gap-2 px-4 py-2.5 text-muted-foreground">
                    <span className="font-mono text-[10px] font-semibold text-violet-500">1</span>
                    <span><strong className="text-foreground">Master Profile:</strong> Stores all your real experience</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2.5 text-muted-foreground">
                    <span className="font-mono text-[10px] font-semibold text-violet-500">2</span>
                    <span><strong className="text-foreground">1-Click Tailor:</strong> Extracts facts that match this JD</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2.5 text-muted-foreground">
                    <span className="font-mono text-[10px] font-semibold text-violet-500">3</span>
                    <span><strong className="text-foreground">Defend Cards:</strong> Talking points for your interview</span>
                  </div>
                </div>

                {/* Before vs After Visual Transformation */}
                <div className="grid divide-y divide-border md:grid-cols-2 md:divide-x md:divide-y-0">
                  {/* Before Box */}
                  <div className="p-5 sm:p-6 flex flex-col justify-between">
                    <div>
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-destructive">
                          <XCircle className="size-3.5" aria-hidden="true" />
                          Before: Generic Bullet
                        </div>
                        <span className="font-mono text-[11px] rounded bg-destructive/10 px-1.5 py-0.5 font-semibold text-destructive">
                          {activeEvidence.before.atsScore}% ATS Match
                        </span>
                      </div>
                      <p className="text-sm italic leading-relaxed text-muted-foreground">
                        &ldquo;{activeEvidence.before.text}&rdquo;
                      </p>
                    </div>

                    <div className="mt-4 space-y-1.5 border-t border-border pt-3">
                      {activeEvidence.before.issues.slice(0, 2).map((issue) => (
                        <div key={issue} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <XCircle className="size-3 shrink-0 text-destructive" aria-hidden="true" />
                          <span className="truncate">{issue}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* After Box */}
                  <div className="p-5 sm:p-6 flex flex-col justify-between">
                    <div>
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 className="size-3.5" aria-hidden="true" />
                          After: Tailored with Real Evidence
                        </div>
                        <span className="font-mono text-[11px] rounded bg-emerald-500/10 px-1.5 py-0.5 font-semibold text-emerald-700 dark:text-emerald-400">
                          {activeEvidence.after.atsScore}% ATS Match
                        </span>
                      </div>
                      <p className="text-sm font-medium leading-relaxed text-foreground">
                        &ldquo;{activeEvidence.after.text}&rdquo;
                      </p>

                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {activeEvidence.after.keywords.slice(0, 4).map((kw) => (
                          <span
                            key={kw}
                            className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-foreground"
                          >
                            {kw}
                          </span>
                        ))}
                        {activeEvidence.after.metrics.map((m) => (
                          <span
                            key={m}
                            className="rounded border border-emerald-500/25 bg-emerald-500/10 px-1.5 py-0.5 font-mono text-[10px] text-emerald-700 dark:text-emerald-400"
                          >
                            {m}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mt-4 rounded-lg border border-border bg-muted/60 p-3">
                      <div className="flex items-start gap-2 text-xs text-muted-foreground">
                        <ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-violet-500" />
                        <div>
                          <strong className="text-foreground">Defend Card: </strong>
                          <span>{activeEvidence.after.defendCard.advice}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Strip with 2 Modes (Tailor vs Booster) */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3 bg-muted/20 text-xs">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Zap className="size-3.5 text-violet-500 shrink-0" />
                    <span>
                      <strong className="text-foreground">Two Ways to Win:</strong> <strong>Tailor</strong> adapts your bullets to match the job, or <strong>ATS Booster</strong> keeps your existing PDF layout pixel-identical while injecting keywords.
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to="/resume-tailor">
                      <Button size="sm" className="h-8 gap-1.5 text-xs font-semibold">
                        <Wand2 className="size-3.5" />
                        Tailor resume (3 free credits)
                        <ArrowRight className="size-3" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {activeHeroPillar === "tracker" && (
              <div className="rounded-xl border border-border bg-card shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <span className="size-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
                    <span className="text-xs font-semibold text-foreground">Application Tracker & Pipeline</span>
                    <span className="text-[11px] text-muted-foreground">Auto-tracks every tailored PDF variant</span>
                  </div>
                  <span className="font-mono text-[11px] text-muted-foreground">
                    Never wonder which resume version you sent
                  </span>
                </div>

                <div className="p-5 sm:p-6">
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {/* Column 1: Bookmarked */}
                    <div className="rounded-lg border border-border bg-muted/30 p-3">
                      <div className="mb-2.5 flex items-center justify-between text-xs font-semibold text-foreground">
                        <span>Bookmarked</span>
                        <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px]">12</span>
                      </div>
                      <div className="space-y-2">
                        <div className="rounded-md border border-border bg-card p-2.5 shadow-xs">
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs font-semibold text-foreground">Linear</span>
                            <span className="font-mono text-[10px] text-emerald-600 dark:text-emerald-400">94% Match</span>
                          </div>
                          <p className="mt-1 text-[11px] text-muted-foreground">Senior Frontend Engineer</p>
                          <span className="mt-2 block text-[10px] font-mono text-muted-foreground">$180k · Remote</span>
                        </div>
                        <div className="rounded-md border border-border bg-card p-2.5 shadow-xs">
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs font-semibold text-foreground">Stripe</span>
                            <span className="font-mono text-[10px] text-emerald-600 dark:text-emerald-400">96% Match</span>
                          </div>
                          <p className="mt-1 text-[11px] text-muted-foreground">Staff Distributed Systems</p>
                          <span className="mt-2 block text-[10px] font-mono text-muted-foreground">$220k · Remote</span>
                        </div>
                      </div>
                    </div>

                    {/* Column 2: Applied */}
                    <div className="rounded-lg border border-border bg-muted/30 p-3">
                      <div className="mb-2.5 flex items-center justify-between text-xs font-semibold text-foreground">
                        <span>Applied</span>
                        <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px]">8</span>
                      </div>
                      <div className="space-y-2">
                        <div className="rounded-md border border-border bg-card p-2.5 shadow-xs">
                          <span className="text-xs font-semibold text-foreground">Vercel</span>
                          <p className="mt-1 text-[11px] text-muted-foreground">Core Infrastructure Engineer</p>
                          <div className="mt-2.5 flex items-center gap-1 rounded border border-violet-500/25 bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-mono text-violet-700 dark:text-violet-400">
                            <FileText className="size-3 shrink-0" />
                            <span className="truncate">Vercel_Tailored_v2.pdf</span>
                          </div>
                        </div>
                        <div className="rounded-md border border-border bg-card p-2.5 shadow-xs">
                          <span className="text-xs font-semibold text-foreground">Supabase</span>
                          <p className="mt-1 text-[11px] text-muted-foreground">Postgres Storage Lead</p>
                          <div className="mt-2.5 flex items-center gap-1 rounded border border-violet-500/25 bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-mono text-violet-700 dark:text-violet-400">
                            <FileText className="size-3 shrink-0" />
                            <span className="truncate">Supabase_ATS_Boosted.pdf</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Column 3: Interviewing */}
                    <div className="rounded-lg border border-border bg-muted/30 p-3">
                      <div className="mb-2.5 flex items-center justify-between text-xs font-semibold text-foreground">
                        <span>Interviewing</span>
                        <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px]">3</span>
                      </div>
                      <div className="space-y-2">
                        <div className="rounded-md border border-border bg-card p-2.5 shadow-xs">
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs font-semibold text-foreground">Datadog</span>
                            <span className="rounded bg-amber-500/10 px-1.5 py-0.5 font-mono text-[9px] font-semibold text-amber-700 dark:text-amber-400">
                              Tech Round
                            </span>
                          </div>
                          <p className="mt-1 text-[11px] text-muted-foreground">Staff Platform Engineer</p>
                          <div className="mt-2.5 flex items-center gap-1 rounded border border-amber-500/25 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-mono text-amber-700 dark:text-amber-400">
                            <ShieldCheck className="size-3 shrink-0" />
                            <span className="truncate">Defend Cards Attached</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Column 4: Offered */}
                    <div className="rounded-lg border border-border bg-muted/30 p-3">
                      <div className="mb-2.5 flex items-center justify-between text-xs font-semibold text-foreground">
                        <span>Offered</span>
                        <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px]">1</span>
                      </div>
                      <div className="space-y-2">
                        <div className="rounded-md border border-emerald-500/30 bg-card p-2.5 shadow-xs">
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs font-semibold text-foreground">GitHub</span>
                            <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 font-mono text-[9px] font-semibold text-emerald-700 dark:text-emerald-400">
                              Offer
                            </span>
                          </div>
                          <p className="mt-1 text-[11px] text-muted-foreground">Senior Backend Engineer</p>
                          <div className="mt-2.5 flex items-center gap-1 rounded border border-emerald-500/25 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-mono font-semibold text-emerald-700 dark:text-emerald-400">
                            <CheckCircle2 className="size-3 shrink-0" />
                            <span>$210k base + equity</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4 text-xs text-muted-foreground">
                    <span>Every application automatically binds the exact tailored resume and Defend Cards so callbacks never catch you unprepared.</span>
                    <Link to="/job-tracker">
                      <Button size="sm" className="h-8 gap-1.5 text-xs font-semibold">
                        <ListTodo className="size-3.5" />
                        Open job tracker
                        <ArrowRight className="size-3" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Rail>
      </Band>

      {/* 2. SOURCE ROW */}
      <Band>
        <Rail>
          <p className="px-6 pt-8 pb-5 text-center text-[11px] tracking-tight text-muted-foreground sm:px-10">
            Verified openings sourced hourly from
          </p>
          <div className="grid grid-cols-2 divide-x divide-y divide-border border-t border-border sm:grid-cols-4 lg:divide-y-0">
            {SOURCES.map((name) => (
              <div
                key={name}
                className="flex items-center justify-center py-7 text-sm font-semibold tracking-tight text-muted-foreground"
              >
                {name}
              </div>
            ))}
          </div>
        </Rail>
      </Band>

      {/* 3. THREE-STEP VALUE GRID */}
      <Band>
        <Rail>
          <div className="px-6 py-16 sm:px-10 sm:py-20">
            <Display>
              JobCues <strong>finds</strong> verified roles,
              <br />
              <strong>rewrites</strong> your resume against each one,
              <br />
              and <strong>tracks</strong> every application
            </Display>
          </div>
        </Rail>
        <hr className="border-t border-border" />
        <Rail>
          <div className="grid divide-y divide-border lg:grid-cols-3 lg:divide-x lg:divide-y-0">
            <div className="flex flex-col p-6 sm:p-8">
              <div
                className={`mb-6 flex flex-1 flex-col justify-center gap-2 rounded-lg p-4 ${GRID_PAPER}`}
              >
                {[
                  ["Senior Frontend Engineer", "2d ago"],
                  ["Backend Engineer, Payments", "4d ago"],
                  ["ML Platform Engineer", "6d ago"],
                ].map(([role, age]) => (
                  <div
                    key={role}
                    className="flex items-center justify-between gap-2 rounded-md border border-border bg-card px-2.5 py-2"
                  >
                    <span className="truncate text-[11px] font-medium text-foreground">{role}</span>
                    <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                      {age}
                    </span>
                  </div>
                ))}
              </div>
              <Caption icon={Briefcase} title="Postings, made readable.">
                Roles come straight from company boards, filtered to the last 7 days, then rewritten
                into one short brief each. No walls of boilerplate, no stale reposts.
              </Caption>
            </div>

            <div className="flex flex-col p-6 sm:p-8">
              <div className="mb-6 flex flex-1 flex-col justify-center gap-2 rounded-lg border border-border bg-muted/40 p-4">
                <div className="h-2 w-full rounded-full bg-border" />
                <div className="h-2 w-4/5 rounded-full bg-border" />
                <div className="mt-2 h-2 w-full rounded-full bg-violet-500/50" />
                <div className="h-2 w-3/5 rounded-full bg-violet-500/30" />
                <span className="mt-3 font-mono text-[11px] text-muted-foreground">
                  match 96% · pdf ready · 15s
                </span>
              </div>
              <Caption icon={Wand2} title="Evidence-based rewrites.">
                Your real experience, restated with the deliverables, stack, and metrics the job
                description asks for. Nothing invented.
              </Caption>
            </div>

            <div className="flex flex-col p-6 sm:p-8">
              <div className="mb-6 flex flex-1 flex-col justify-center gap-1.5 rounded-lg border border-border bg-muted/40 p-4">
                {["Bookmarked", "Applied", "Interviewing", "Offered"].map((stage, i) => (
                  <div
                    key={stage}
                    className="flex items-center justify-between rounded-md border border-border bg-card px-2.5 py-1.5 text-[11px] text-muted-foreground"
                  >
                    <span>{stage}</span>
                    <span className="font-mono">{[12, 8, 3, 1][i]}</span>
                  </div>
                ))}
              </div>
              <Caption icon={ListTodo} title="Tracked to the offer.">
                Each stage keeps the exact resume variant you submitted, so a callback never catches
                you guessing.
              </Caption>
            </div>
          </div>
        </Rail>
      </Band>

      {/* 4. HONESTY STATEMENT */}
      <Band>
        <Rail className="px-6 py-20 text-center sm:px-10 sm:py-28">
          <Quote className="mx-auto mb-6 size-5 text-muted-foreground" aria-hidden="true" />
          <p className="mx-auto max-w-2xl text-2xl font-semibold leading-snug tracking-tight text-foreground sm:text-3xl">
            No invented companies. No fake certifications. Every bullet we promote is one you can
            defend in the interview.
          </p>
          <p className="mt-5 text-xs text-muted-foreground">
            The JobCues tailoring policy, enforced on every generation
          </p>
        </Rail>
      </Band>

      {/* 5. LIVE JOBS */}
      <Band>
        <Rail>
          <div className="flex flex-col justify-between gap-5 px-6 py-14 sm:flex-row sm:items-end sm:px-10 sm:py-16">
            <div>
              <Eyebrow icon={Briefcase} className="mb-5">
                Live feed
              </Eyebrow>
              <Display>
                <strong>Every posting arrives read</strong>
                <br />
                before you open it
              </Display>
              <p className="mt-6 max-w-xl text-sm leading-relaxed text-muted-foreground">
                We do not re-list the raw advert. Every posting is put through the same reader and
                comes back as a short, fixed brief, so you can judge a role in seconds instead of
                working through screens of boilerplate to find the four facts that decide it.
              </p>
            </div>
            <Link to="/jobs">
              <Button variant="outline" className="h-10 gap-2 font-semibold">
                View all jobs
                <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>

          <hr className="border-t border-border" />
          <div className="grid divide-y divide-border sm:grid-cols-2 sm:divide-x lg:grid-cols-4 lg:divide-y-0">
            {BRIEF_PARTS.map((part) => (
              <div key={part.label} className="p-5 sm:p-6">
                <Caption icon={part.icon} title={`${part.label}.`}>
                  {part.body}
                </Caption>
              </div>
            ))}
          </div>
          <hr className="border-t border-border" />
          <div className="grid gap-4 p-6 sm:p-10 md:grid-cols-2">
            {jobsLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-36 animate-pulse rounded-xl border border-border bg-muted/40"
                />
              ))
            ) : jobsData && jobsData.items.length > 0 ? (
              jobsData.items.slice(0, 4).map((job) => (
                <JobCard
                  key={job.id}
                  id={job.id}
                  slug={job.slug}
                  title={job.title}
                  company={job.company}
                  seniority={job.seniority}
                  yearsMin={job.yearsMin}
                  yearsMax={job.yearsMax}
                  workMode={job.workMode}
                  locationPrimary={job.locationPrimary}
                  country={job.country}
                  compMin={job.compMin}
                  compMax={job.compMax}
                  compCurrency={job.compCurrency}
                  compPeriod={job.compPeriod}
                  postedAt={job.postedAt}
                  oneLiner={job.oneLiner}
                  topSkills={job.topSkills || []}
                  isSaved={savedSet.has(job.id)}
                  minimal={true}
                  onToggleSave={(e) => {
                    e.preventDefault();
                    if (!session?.user) {
                      openAuthModal({
                        title: "Save Jobs",
                        description: "Sign in to save and organize jobs to your tracker.",
                      });
                      return;
                    }
                    toggleSaveMutation.mutate(job.id);
                  }}
                />
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-border p-8 text-center md:col-span-2">
                <Briefcase className="mx-auto mb-2 size-6 text-muted-foreground" />
                <p className="text-sm font-semibold text-foreground">
                  Verified tech openings syncing
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Remote, junior, and senior software roles land here as soon as the sync finishes.
                </p>
                <Link to="/jobs">
                  <Button size="sm" className="mt-4">
                    Explore job board
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </Rail>
      </Band>

      {/* 6. SUITE BENTO */}
      <Band>
        <Rail className="px-6 py-16 text-center sm:px-10 sm:py-20">
          <Eyebrow icon={Layers} className="mb-6">
            Platform
          </Eyebrow>
          <Display className="mx-auto max-w-2xl">
            <strong>Built for people</strong>
            <br />
            who apply seriously
          </Display>
          <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-muted-foreground">
            Four tools that share one profile, one credit balance, and one tracker.
          </p>
        </Rail>
        <hr className="border-t border-border" />
        <Rail>
          <div className="grid divide-y divide-border sm:grid-cols-2 sm:divide-x">
            {SUITE.map(({ icon: Icon, title, body, to, mock }) => (
              <Link
                key={title}
                to={to}
                className="group flex flex-col p-6 transition hover:bg-muted/40 sm:p-8"
              >
                <div
                  className={`mb-6 flex min-h-44 items-center justify-center rounded-lg p-6 ${GRID_PAPER}`}
                >
                  {mock}
                </div>
                <Caption icon={Icon} title={`${title}.`}>
                  {body}
                </Caption>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-foreground">
                  Open
                  <ArrowRight className="size-3.5 transition group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </Rail>
      </Band>

      {/* 7. TEMPLATES */}
      <Band>
        <Rail className="px-6 py-16 text-center sm:px-10 sm:py-20">
          <Eyebrow icon={FileCheck2} className="mb-6">
            12+ ATS layouts
          </Eyebrow>
          <Display className="mx-auto max-w-2xl">
            <strong>Layouts built to be read</strong>
            <br />
            by scanners and by people
          </Display>
          <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-muted-foreground">
            Every template renders a clean, professional PDF that ATS parsers read without trouble.
          </p>
        </Rail>
        <hr className="border-t border-border" />
        <Rail>
          <div className="grid grid-cols-2 divide-x divide-y divide-border sm:grid-cols-3 lg:grid-cols-6">
            {TEMPLATES.map((tmpl) => (
              <button
                key={tmpl.id}
                type="button"
                onClick={() => setSelectedTemplate(tmpl.id)}
                aria-pressed={selectedTemplate === tmpl.id}
                className={`group cursor-pointer p-3 text-left transition ${
                  selectedTemplate === tmpl.id ? "bg-muted/70" : "hover:bg-muted/40"
                }`}
              >
                <div
                  className={`aspect-3/4 overflow-hidden rounded-md border bg-muted/50 ${
                    selectedTemplate === tmpl.id ? "border-violet-500" : "border-border"
                  }`}
                >
                  <img
                    src={`/templates/${tmpl.id}.webp`}
                    alt={`${tmpl.name} resume template preview — ${tmpl.desc}`}
                    width={300}
                    height={400}
                    className="h-full w-full object-cover object-top transition-transform group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                </div>
                <h3 className="mt-2.5 truncate text-xs font-semibold text-foreground">
                  {tmpl.name}
                </h3>
                <p className="line-clamp-1 text-[11px] text-muted-foreground">{tmpl.desc}</p>
              </button>
            ))}
          </div>
        </Rail>
        <hr className="border-t border-border" />
        <Rail className="px-6 py-8 text-center sm:px-10">
          <Link to="/resume-tailor">
            <Button variant="outline" className="h-10 gap-2 font-semibold">
              <Wand2 className="size-4 text-violet-500" />
              Try every template with your resume
            </Button>
          </Link>
        </Rail>
      </Band>

      {/* 8. DEVELOPER / MCP (DARK) */}
      <Band dark>
        <Rail dark className="px-6 py-16 text-center sm:px-10 sm:py-20">
          <Eyebrow icon={Terminal} dark className="mb-6">
            Model Context Protocol
          </Eyebrow>
          <Display dark className="mx-auto max-w-3xl">
            <strong>Run your job hunt</strong>
            <br />
            from Claude, Cursor, or curl
          </Display>
          <p className="mx-auto mt-5 max-w-lg text-sm leading-relaxed text-neutral-400">
            Query verified jobs, check match scores, and export tailored PDFs without ever leaving
            your editor. Included in Pro and Ultimate.
          </p>
        </Rail>

        <hr className="border-t border-white/10" />
        <Rail dark>
          <div className="grid divide-y divide-white/10 lg:grid-cols-12 lg:divide-x lg:divide-y-0">
            <div className="divide-y divide-white/10 lg:col-span-5">
              <div className="p-6 sm:p-8">
                <Caption icon={Terminal} title="Native MCP server." dark>
                  One block in <code className="font-mono">~/.cursor/mcp.json</code> gives your
                  assistant <code className="font-mono">jobcues_search_jobs</code>,{" "}
                  <code className="font-mono">jobcues_match_score</code>, and{" "}
                  <code className="font-mono">jobcues_tailor_resume</code>.
                </Caption>
              </div>
              <div className="p-6 sm:p-8">
                <Caption icon={Code2} title="Deterministic PDF engine." dark>
                  Every run renders the same document in milliseconds, with consistent margins and
                  ATS-safe geometry, straight from a script or CI job.
                </Caption>
              </div>
              <div className="p-6 sm:p-8">
                <Caption icon={Blocks} title="REST when you want it." dark>
                  The same operations are exposed over plain HTTP with a bearer key, so any language
                  or agent framework can drive them.
                </Caption>
              </div>
              <div className="flex gap-2.5 p-6 sm:p-8">
                <Link to="/api-docs" className="flex-1">
                  <Button
                    variant="outline"
                    className="h-10 w-full gap-2 border-white/15 bg-white/5 font-semibold text-white hover:bg-white/10"
                  >
                    <Code2 className="size-4" />
                    API docs
                  </Button>
                </Link>
                <Link to="/api-keys" className="flex-1">
                  <Button className="h-10 w-full gap-2 bg-white font-semibold text-neutral-900 hover:bg-neutral-200">
                    Get API key
                  </Button>
                </Link>
              </div>
            </div>

            <div className="flex flex-col lg:col-span-7">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-4 py-3">
                <span className="font-mono text-[11px] text-neutral-500">
                  jobcues-developer-hub
                </span>
                <div className="flex items-center gap-1 font-mono text-[11px]">
                  {(
                    [
                      ["mcp", "mcp.json"],
                      ["agent", "agent"],
                      ["curl", "curl"],
                    ] as const
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setActiveMcpTab(key)}
                      className={`cursor-pointer rounded-md px-2.5 py-1 transition ${
                        activeMcpTab === key
                          ? "bg-white/10 text-white"
                          : "text-neutral-500 hover:text-neutral-300"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="relative flex-1 overflow-x-auto p-5 font-mono text-xs leading-relaxed sm:p-6">
                <button
                  type="button"
                  onClick={() => {
                    const textToCopy =
                      activeMcpTab === "mcp"
                        ? mcpConfigCode
                        : activeMcpTab === "agent"
                          ? agentPromptCode
                          : curlApiCode;
                    copyToClipboard(textToCopy);
                  }}
                  className="absolute top-4 right-4 flex cursor-pointer items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] text-neutral-300 transition hover:bg-white/10 hover:text-white"
                >
                  {copiedMcp ? (
                    <>
                      <Check className="size-3 text-emerald-400" />
                      <span className="text-emerald-400">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="size-3" />
                      <span>Copy</span>
                    </>
                  )}
                </button>

                {activeMcpTab === "mcp" && (
                  <pre className="overflow-x-auto text-neutral-300">{mcpConfigCode}</pre>
                )}
                {activeMcpTab === "agent" && (
                  <pre className="overflow-x-auto whitespace-pre-wrap text-neutral-300">
                    {agentPromptCode}
                  </pre>
                )}
                {activeMcpTab === "curl" && (
                  <pre className="overflow-x-auto whitespace-pre-wrap text-neutral-300">
                    {curlApiCode}
                  </pre>
                )}
              </div>

              <div className="flex items-center justify-between border-t border-white/10 px-5 py-3 text-[11px] text-neutral-500">
                <span className="flex items-center gap-2">
                  <span className="size-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                  MCP server v1.0.4 online
                </span>
                <Link to="/api-docs" className="text-neutral-300 hover:text-white">
                  Explore RPC endpoints &rarr;
                </Link>
              </div>
            </div>
          </div>
        </Rail>
      </Band>

      {/* 9. PRICING */}
      <Band id="pricing">
        <Rail className="px-6 py-16 text-center sm:px-10 sm:py-20">
          <Eyebrow icon={LineChart} className="mb-6">
            Pricing
          </Eyebrow>
          <Display className="mx-auto max-w-2xl">
            <strong>Simple monthly plans</strong>
            <br />
            for every application pace
          </Display>
          <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-muted-foreground">
            Upgrade, downgrade, or cancel at any time. Credits refresh every month.
          </p>
        </Rail>
        <hr className="border-t border-border" />
        <Rail>
          <div className="grid divide-y divide-border lg:grid-cols-3 lg:divide-x lg:divide-y-0">
            {CREDIT_PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`flex flex-col p-7 sm:p-8 ${plan.featured ? "bg-muted/40" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-foreground">{plan.name}</h3>
                  {plan.featured ? (
                    <span className="rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-400">
                      Most popular
                    </span>
                  ) : null}
                </div>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{plan.body}</p>

                <div className="mt-6 border-b border-border pb-6">
                  <div className="mb-1.5 flex items-center gap-2">
                    <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-600 dark:text-green-400">
                      Launch offer
                    </span>
                    <span className="text-xs text-muted-foreground line-through">
                      ${plan.futurePriceUsd}/mo
                    </span>
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-4xl font-semibold tracking-tight text-foreground">
                      ${plan.priceUsd}
                    </span>
                    <span className="text-xs text-muted-foreground">/ month</span>
                    <span className="ml-auto font-mono text-[11px] text-muted-foreground">
                      {plan.meta}
                    </span>
                  </div>
                </div>

                <ul className="mt-6 flex-1 space-y-2.5 text-xs leading-relaxed text-muted-foreground">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check
                        className="mt-0.5 size-3.5 shrink-0 text-violet-500"
                        aria-hidden="true"
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={handleSignupCta}
                  variant={plan.featured ? "default" : "outline"}
                  className="mt-8 h-10 w-full cursor-pointer font-semibold"
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </Rail>
        <hr className="border-t border-border" />
        <Rail>
          <div className="flex flex-col items-center gap-4 px-6 py-10 text-center sm:flex-row sm:items-center sm:justify-between sm:px-10 sm:text-left">
            <div>
              <p className="text-sm font-semibold text-foreground">Start for free</p>
              <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                The job board is free for everyone. Try the AI tools with 3 credits on signup — no card required.
              </p>
            </div>
            <Button onClick={handleSignupCta} className="shrink-0 cursor-pointer font-semibold">
              Get started free
            </Button>
          </div>
        </Rail>
      </Band>

      {/* 10. MASTER CAREER PROFILE + STEPS */}
      <Band>
        <Rail>
          <div className="grid gap-10 px-6 py-16 sm:px-10 sm:py-20 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6">
              <Eyebrow icon={UserCheck} className="mb-6">
                Master Career Profile
              </Eyebrow>
              <Display>
                <strong>Write it all down once.</strong>
                <br />
                Tailor selects per job
              </Display>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-muted-foreground">
                Upload your existing resume and JobCues parses it into an editable career archive.
                Then keep adding: all 20+ projects, every skill you have used, every role and
                number. It is an archive, not a resume, so none of it has to fit on a page.
              </p>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
                When you tailor for a job, the AI reads the whole archive and selects only the items
                that match that description, so each resume stays focused instead of generic.
              </p>
              <Link to="/account">
                <Button className="mt-7 h-10 gap-2 font-semibold">
                  <UserCheck className="size-4" />
                  Build your Master Profile
                  <ArrowRight className="size-4" />
                </Button>
              </Link>
            </div>

            <div className="lg:col-span-6">
              <div className={`rounded-xl p-5 sm:p-8 ${GRID_PAPER}`}>
                <div className="rounded-lg border border-border bg-card shadow-sm">
                  <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
                    <span className="text-xs font-semibold text-foreground">Career archive</span>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      24 entries · saved
                    </span>
                  </div>
                  <div className="divide-y divide-border">
                    {[
                      ["Experience", "Senior Frontend Engineer, 2022–now", true],
                      ["Project", "Realtime pricing dashboard, 45k users", true],
                      ["Project", "Kafka consumer rewrite, p99 18ms", false],
                      ["Project", "Internal design system, 40 components", false],
                      ["Skills", "React · TypeScript · Go · Postgres · Kafka", true],
                      ["Education", "BSc Computer Science", true],
                    ].map(([label, text, picked]) => (
                      <div key={text as string} className="flex items-start gap-3 px-4 py-2.5">
                        <span className="w-16 shrink-0 font-mono text-[10px] text-muted-foreground">
                          {label}
                        </span>
                        <span
                          className={`flex-1 truncate text-[11px] ${picked ? "text-foreground" : "text-muted-foreground/60"}`}
                        >
                          {text}
                        </span>
                        {picked ? (
                          <Check
                            className="mt-0.5 size-3 shrink-0 text-violet-500"
                            aria-hidden="true"
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-border px-4 py-2.5 font-mono text-[10px] text-muted-foreground">
                    <Check className="mr-1.5 inline size-3 align-[-0.15em] text-violet-500" />
                    selected for: Senior Frontend Engineer
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Rail>
        <hr className="border-t border-border" />
        <Rail>
          <div className="grid divide-y divide-border lg:grid-cols-3 lg:divide-x lg:divide-y-0">
            {STEPS.map((step) => (
              <div key={step.n} className="p-6 sm:p-8">
                <div className="mb-3 flex items-center gap-2.5">
                  <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
                    {step.n}
                  </span>
                  <span className="text-sm font-semibold text-foreground">{step.title}</span>
                </div>
                <p className="text-[13px] leading-relaxed text-muted-foreground">{step.body}</p>
              </div>
            ))}
          </div>
        </Rail>
      </Band>

      {/* 11. FAQ */}
      <Band>
        <Rail className="px-6 py-16 sm:px-10 sm:py-20">
          <div className="mb-10 text-center">
            <Eyebrow icon={HelpCircle} className="mb-6">
              FAQ
            </Eyebrow>
            <Display>
              <strong>Questions</strong>, answered
            </Display>
          </div>

          <Accordion className="mx-auto flex max-w-3xl flex-col gap-2.5">
            {FAQS.map((faq) => (
              <AccordionItem
                key={faq.value}
                value={faq.value}
                className="rounded-xl border border-border bg-card px-5"
              >
                <AccordionTrigger className="py-4 text-left text-sm font-semibold text-foreground">
                  {faq.q}
                </AccordionTrigger>
                <AccordionPanel className="pb-5 text-[13px] leading-relaxed text-muted-foreground">
                  {faq.a}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-8 text-center">
            <Link
              to="/faq"
              className="inline-flex items-center gap-1 text-xs font-semibold text-foreground hover:underline"
            >
              Read the complete FAQ and ATS guide
              <ArrowRight className="size-3.5" />
            </Link>
          </div>
        </Rail>
      </Band>

      {/* 12. FINAL CTA (DARK) */}
      <Band dark>
        <Rail dark>
          <div className="grid gap-8 px-6 py-16 sm:px-10 sm:py-20 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <Display dark>
                <strong>Apply with a resume</strong>
                <br />
                that clears the scanner
              </Display>
            </div>
            <div className="flex flex-wrap items-center gap-2.5 lg:col-span-5 lg:justify-end">
              <Link to="/resume-tailor">
                <Button className="h-10 gap-2 bg-white font-semibold text-neutral-900 hover:bg-neutral-200">
                  <Wand2 className="size-4" />
                  Tailor your resume
                </Button>
              </Link>
              <Link to="/jobs">
                <Button
                  variant="outline"
                  className="h-10 gap-2 border-white/15 bg-white/5 font-semibold text-white hover:bg-white/10"
                >
                  <Briefcase className="size-4" />
                  Explore jobs
                </Button>
              </Link>
            </div>
          </div>
        </Rail>
        <hr className="border-t border-white/10" />
        <Rail dark>
          <div className="grid divide-y divide-white/10 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            <div className="flex items-center justify-center gap-2 py-5 text-[11px] text-neutral-400">
              <Sparkles className="size-3.5 text-violet-400" aria-hidden="true" />3 free credits on
              signup
            </div>
            <div className="flex items-center justify-center gap-2 py-5 text-[11px] text-neutral-400">
              <Lock className="size-3.5 text-violet-400" aria-hidden="true" />
              Cancel any time
            </div>
            <div className="flex items-center justify-center gap-2 py-5 text-[11px] text-neutral-400">
              <Cpu className="size-3.5 text-violet-400" aria-hidden="true" />
              MCP and REST API on Pro
            </div>
          </div>
        </Rail>
      </Band>
    </div>
  );
}
